import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage, Territory } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EditableText, Modal } from "@mapmycustomers/ui";

import DownloadMutipleEntitiesModal from "@app/component/DownloadMultipleEntitiesModal";
import {
  getMultiPin,
  getTerritory,
  getTerritoryVisibleEntities,
} from "@app/scene/map/store/selectors";
import {
  downloadTerritoryRecords,
  exitTerritoryMode,
  setTerritoryMode,
  updateTerritory,
} from "@app/scene/map/store/territoryMode/actions";
import { RootState } from "@app/store/rootReducer";
import { deleteTerritory } from "@app/store/territories/actions";
import DownloadEntitiesMapping from "@app/types/DownloadEntitiesMapping";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import TerritoryMenu from "../TerritoryMenu";

import styles from "./Title.module.scss";

interface Props {
  multiPin?: MultiPin;
  onChangeMode: typeof setTerritoryMode;
  onDeleteTerritory: typeof deleteTerritory.request;
  onDownloadTerritoryRecords: typeof downloadTerritoryRecords;
  onExitTerritoryMode: typeof exitTerritoryMode.request;
  onUpdateTerritory: typeof updateTerritory.request;
  territory?: Territory;
  visibleEntities: EntityTypesSupportedByMapsPage[];
}

const messages = defineMessages({
  multiPin: {
    id: "map.territoryMode.sidebar.title.multiPin",
    defaultMessage: "Multiple Records",
    description: "Records list for multi-pin - title",
  },
});

const Title: React.FC<Props> = ({
  multiPin,
  onChangeMode,
  onDeleteTerritory,
  onDownloadTerritoryRecords,
  onExitTerritoryMode,
  onUpdateTerritory,
  territory,
  visibleEntities,
}) => {
  const intl = useIntl();

  const analytics = useExtendedAnalytics(
    ["Edit", territory?.territoryDetail.type === "points" ? "Draw" : "Smart"],
    useAnalytics()
  );

  const isMultiPinMode = !!multiPin;

  const [renaming, setRenaming] = useState(false);
  const [downloadModal, showDownloadModal, hideDownloadModal] = useBoolean();
  const handleRename = useCallback(() => setRenaming(true), []);

  const handleNameChange = useCallback(
    (name: string) => {
      if (!territory) {
        return;
      }

      onUpdateTerritory?.({
        onSuccess: () => analytics.completed(""),
        territory: { ...territory, name },
      });
    },
    [analytics, onUpdateTerritory, territory]
  );

  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean();

  const handleDeleteTerritory = useCallback(() => {
    if (!territory) {
      return;
    }
    onDeleteTerritory?.({ territoryId: territory.id });
    onExitTerritoryMode();
    analytics.cancelled("Deleted");
  }, [analytics, onDeleteTerritory, territory, onExitTerritoryMode]);

  const handleEdit = useCallback(() => {
    onChangeMode("edit");
  }, [onChangeMode]);

  const handleExport = useCallback(
    (entityColumnsMap: DownloadEntitiesMapping) => {
      analytics.clicked(["Export"]);
      onDownloadTerritoryRecords(entityColumnsMap);
    },
    [analytics, onDownloadTerritoryRecords]
  );

  return isMultiPinMode ? (
    <>{intl.formatMessage(messages.multiPin)}</>
  ) : (
    <div className={styles.titleContainer}>
      <span className={styles.title}>
        <Tooltip title={territory?.name}>
          <EditableText
            editable={!!territory?.accessStatus.update}
            editing={renaming}
            fieldClassName={styles.nameField}
            onChange={handleNameChange}
            onEditingChange={setRenaming}
            required
            size="default"
            tooltipTitle={intl.formatMessage({
              id: "map.territoryMode.sidebar.title.tooltip",
              defaultMessage: "Change Territory Name",
              description: "Territory name tooltip on the View Territory page",
            })}
            value={territory?.name ?? ""}
          />
        </Tooltip>
      </span>
      <TerritoryMenu
        onDelete={showDeleteConfirmation}
        onEdit={handleEdit}
        onExport={visibleEntities.length < 1 ? undefined : showDownloadModal}
        onRename={handleRename}
        territory={territory}
      />
      {downloadModal && (
        <DownloadMutipleEntitiesModal
          onDownload={handleExport}
          onHide={hideDownloadModal}
          visibleEntities={visibleEntities}
        />
      )}
      {deleteConfirmationVisible && (
        <Modal
          okButtonProps={{ danger: true, type: "default" }}
          okText={intl.formatMessage({
            id: "map.territoryMode.sidebar.deleteConfirmation.ok",
            defaultMessage: "Delete Territory",
            description: "Ok button label in Delete Territory confirmation on the Territory page",
          })}
          onCancel={hideDeleteConfirmation}
          onOk={handleDeleteTerritory}
          open
          title={intl.formatMessage({
            id: "map.territoryMode.sidebar.deleteConfirmation",
            defaultMessage: "Confirm territory deletion",
            description: "Title of the Delete Territory confirmation on the Territory page",
          })}
        >
          {intl.formatMessage({
            id: "map.territoryMode.sidebar.deleteConfirmation.description",
            defaultMessage:
              "This action will permanently delete this territory. Any records that are shared via this territory will not be deleted, but they may no longer be visible or editable to some users.",
            description: "Description of the Delete Territory confirmation on the Territory page",
          })}
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  multiPin: getMultiPin(state),
  territory: getTerritory(state),
  visibleEntities: getTerritoryVisibleEntities(state),
});

const mapDispatchToProps = {
  onChangeMode: setTerritoryMode,
  onDeleteTerritory: deleteTerritory.request,
  onDownloadTerritoryRecords: downloadTerritoryRecords,
  onExitTerritoryMode: exitTerritoryMode.request,
  onUpdateTerritory: updateTerritory.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Title);
